import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const OrderSummary = ({
  items, total, discount, promoApplied, shippingMethod, shippingCost, orderTotal, pickupLocation,
  promoCode, setPromoCode, promoError, setPromoError, applyPromoCode, setPromoApplied,
  paymentMethod, setPaymentMethod, isProcessing, validateForm, handleStripeCheckout,
  createOrder, onApprove, setStep, isCheckout
}) => (
  <div className="lg:col-span-2">
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="p-8 rounded-2xl border border-border bg-muted/30 sticky top-32">
      <h2 className="text-2xl font-bold text-foreground mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Order Summary</h2>
      
      {/* Item List */}
      <div className="space-y-3 mb-6 pb-6 border-b border-border">
        {items.map(item => (
          <div key={`${item.id}-${item.selectedSize}`} className="flex justify-between text-sm text-foreground/70">
            <span>{item.name} {isCheckout ? `(${item.selectedSize})` : ''} x{item.quantity}</span>
            <span>${(item.itemPrice * item.quantity).toFixed(2)}</span>
          </div>
        ))}
        {!isCheckout && (
          <div className="flex justify-between text-foreground/70">
            <span>Subtotal</span>
            <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
          </div>
        )}
      </div>

      {isCheckout ? (
        <>
          {/* Checkout totals */}
          <div className="space-y-2 mb-6 pb-6 border-b border-border">
            <div className="flex justify-between text-foreground/70">
              <span>Subtotal</span>
              <span data-testid="cart-subtotal">${total.toFixed(2)}</span>
            </div>
            {promoApplied && (
              <div className="flex justify-between text-green-600">
                <span>Discount (BEE10 -10%)</span>
                <span data-testid="cart-discount">-${discount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-foreground/70">
              <span>Shipping ({shippingMethod === 'pickup' ? 'Pickup — Malvern East' : 'Postage'})</span>
              <span data-testid="cart-shipping">{shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}</span>
            </div>
          </div>

          {/* Promo Code */}
          <div className="mb-6 pb-6 border-b border-border">
            <p className="text-sm font-semibold text-foreground mb-2">Promo Code</p>
            <div className="flex flex-col space-y-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => { setPromoCode(e.target.value); setPromoError(''); }}
                placeholder="Enter code"
                className="w-full px-3 py-2 rounded-lg border border-border bg-background focus:outline-none focus:ring-2 focus:ring-primary text-sm"
                data-testid="promo-code-input"
                disabled={promoApplied}
              />
              {promoApplied ? (
                <button
                  onClick={() => { setPromoApplied(false); setPromoCode(''); }}
                  className="w-full px-4 py-2 rounded-lg border border-border text-sm text-foreground hover:bg-muted transition-colors"
                  data-testid="remove-promo-button"
                >
                  Remove
                </button>
              ) : (
                <button
                  onClick={applyPromoCode}
                  className="w-full px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
                  data-testid="apply-promo-button"
                >
                  Apply
                </button>
              )}
            </div>
            {promoError && <p className="text-red-500 text-xs mt-1">{promoError}</p>}
            {promoApplied && <p className="text-green-600 text-xs mt-1">10% discount applied!</p>}
          </div>

          <div className="flex justify-between text-xl font-bold text-foreground mb-6">
            <span>Total</span>
            <span data-testid="cart-total">${orderTotal.toFixed(2)}</span>
          </div>

          {/* Payment Method */}
          <div className="mb-4">
            <p className="text-sm font-semibold text-foreground mb-3">Payment Method</p>
            <div className="flex space-x-3">
              <button onClick={() => setPaymentMethod('stripe')} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all duration-300 flex items-center justify-center space-x-2 ${paymentMethod === 'stripe' ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-border hover:border-primary/50 text-foreground'}`} data-testid="stripe-payment-option">
                <CreditCard className="w-5 h-5" />
                <span>Card</span>
              </button>
              <button onClick={() => setPaymentMethod('paypal')} className={`flex-1 px-4 py-3 rounded-lg border-2 transition-all duration-300 flex items-center justify-center ${paymentMethod === 'paypal' ? 'border-primary bg-primary/5 text-primary font-semibold' : 'border-border hover:border-primary/50 text-foreground'}`} data-testid="paypal-payment-option">
                <span>PayPal</span>
              </button>
            </div>
          </div>

          {paymentMethod === 'stripe' ? (
            <button onClick={() => { if (validateForm()) handleStripeCheckout(); }} disabled={isProcessing} className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold text-lg disabled:opacity-50 disabled:cursor-not-allowed" data-testid="stripe-checkout-button">
              {isProcessing ? 'Processing...' : `Pay $${orderTotal.toFixed(2)}`}
            </button>
          ) : (
            <div data-testid="paypal-buttons-container">
              <PayPalScriptProvider options={{ clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || 'test', currency: 'AUD' }}>
                <PayPalButtons
                  createOrder={(data, actions) => {
                    if (!validateForm()) return actions.reject();
                    return createOrder(data, actions);
                  }}
                  onApprove={onApprove}
                  style={{ layout: 'vertical' }}
                />
              </PayPalScriptProvider>
            </div>
          )}

          <button onClick={() => setStep('cart')} className="w-full mt-3 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-to-cart-button">
            Back to Cart
          </button>
        </>
      ) : (
        <>
          <div className="flex justify-between text-xl font-bold text-foreground mb-6">
            <span>Subtotal</span>
            <span>${total.toFixed(2)}</span>
          </div>
          <button onClick={() => setStep('checkout')} className="w-full px-8 py-4 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 transition-all duration-300 font-semibold text-lg" data-testid="proceed-to-checkout-button">
            Proceed to Checkout
          </button>
        </>
      )}
    </motion.div>
  </div>
);

export default OrderSummary;
